import { inspectMessage } from "@chatbotgang/etude/debug/inspectMessage";
import { css } from "@emotion/react";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { theme } from "@polifonia/theme";
import { Link } from "@tanstack/react-router";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import { useSnackbar } from "notistack";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Empty } from "@/components/Empty";
import { MotifIcon } from "@/components/MotifIcon";
import { TableRowsLoader } from "@/components/TableRowsLoader";
import { Tooltip } from "@/components/Tooltip";
import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { DeleteFeedModal } from "@/pages/applications/pages/productFeeds/components/DeleteFeedModal";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
  margin-bottom: 24px;
`;

const cssButtonContainer = css({
  marginBottom: 24,
  alignItems: "center",
});

const cssEmptyRow = css({ height: "180px" });

const cssEmpty = css({
  minHeight: "64px",
});

const cssActionButton = css({
  padding: 6,
  minWidth: "auto",
  fontSize: 14,
  color: theme.colors.buttonPlainDefault,
  height: "32px",
});

type ProductFeed = InterludeTypes["ProductFeed"];

const StatusBadge: React.FC<{ status: ProductFeed["status"] }> = ({
  status,
}) => {
  const { t } = useTranslation();
  switch (status) {
    case "success":
      return (
        <Badge variant="positive">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="circle_check" />
            <span>{t("productFeed.status.success")}</span>
          </Stack>
        </Badge>
      );
    case "partial_success":
      return (
        <Badge variant="attentive">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="warning" />
            <span>{t("productFeed.status.partialSuccess")}</span>
          </Stack>
        </Badge>
      );
    case "error":
      return (
        <Badge variant="negative">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="circle_cross" />
            <span>{t("productFeed.status.error")}</span>
          </Stack>
        </Badge>
      );
    case "syncing":
      return (
        <Badge variant="superior">
          <Stack direction="row" spacing="4px" sx={{ alignItems: "center" }}>
            <MotifIcon un-i-motif="arrow_sync" />
            <span>{t("productFeed.status.syncing")}</span>
          </Stack>
        </Badge>
      );
    default:
      status satisfies never;
      throw new Error(inspectMessage`Invalid status ${status}`);
  }
};

const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "-";
  }
  return dayjs(dateString).format("YYYY-MM-DD HH:mm");
};

const SourceTypeLabel: React.FC<{
  sourceType: ProductFeed["sourceType"];
}> = ({ sourceType }) => {
  const { t } = useTranslation();
  switch (sourceType) {
    case "file_upload":
      return <>{t("productFeed.sourceType.fileUpload")}</>;
    case "feed_url":
      return <>{t("productFeed.sourceType.feedUrl")}</>;
    default:
      sourceType satisfies never;
      throw new Error(inspectMessage`Invalid source type ${sourceType}`);
  }
};

const DeleteFeedButton: React.FC<{
  feed: ProductFeed;
  onDeleteClick: (feed: ProductFeed) => void;
}> = ({ feed, onDeleteClick }) => {
  const { t } = useTranslation();
  // Interlude rejects deleting a syncing feed (PRODUCT_FEED_IS_SYNCING), so
  // the button is disabled up front and says why, instead of failing after
  // the confirmation.
  const isSyncing = feed.status === "syncing";
  const button = (
    <IconButton
      size="small"
      aria-label={t("common.delete")}
      disabled={isSyncing}
      onClick={() => onDeleteClick(feed)}
    >
      <MotifIcon
        un-i-motif="bin"
        css={css({
          fontSize: 16,
          color: isSyncing
            ? theme.colors.staticFgPlaceholder
            : theme.colors.staticFgNegative,
        })}
      />
    </IconButton>
  );

  if (!isSyncing) return button;

  return (
    <Tooltip
      title={t("productFeed.deleteModal.error.syncing", {
        feedName: feed.name,
      })}
    >
      {/* A disabled button fires no events, so the tooltip needs a wrapper. */}
      <span>{button}</span>
    </Tooltip>
  );
};

export const ProductFeedsPage: React.FC = function ProductFeedsPage() {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const feedsQuery = interlude.productFeed.useListFeeds(
    {},
    {
      refetchInterval: (query) => {
        // Poll every 5 seconds when any feed is syncing
        const hasSyncingFeed = query.state.data?.feeds.some(
          (feed) => feed.status === "syncing",
        );
        return hasSyncingFeed ? 5000 : false;
      },
    },
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedFeed, setSelectedFeed] = useState<ProductFeed | null>(null);
  const [deletingFeedName, setDeletingFeedName] = useState<string>("");

  const deleteFeedMutation = interlude.productFeed.useDeleteFeed(
    {
      params: {
        feedId: selectedFeed?.id ?? 0,
      },
    },
    {
      onSuccess: () => {
        setDeleteModalOpen(false);
        setSelectedFeed(null);
        setDeletingFeedName("");
      },
      onError: (error) => {
        if (error instanceof AxiosError && error.response?.status === 409) {
          enqueueSnackbar(
            t("productFeed.deleteModal.error.syncing", {
              feedName: deletingFeedName || "",
            }),
            {
              variant: "error",
              autoHideDuration: 5000,
            },
          );
        }
      },
    },
  );

  const feeds = useMemo(() => {
    if (feedsQuery.isSuccess && feedsQuery.data) {
      return feedsQuery.data.feeds;
    }
    return [];
  }, [feedsQuery.isSuccess, feedsQuery.data]);

  const handleDeleteClick = (feed: ProductFeed) => {
    setSelectedFeed(feed);
    setDeleteModalOpen(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModalOpen(false);
    setSelectedFeed(null);
    setDeletingFeedName("");
  };

  const handleDeleteModalConfirm = () => {
    if (selectedFeed) {
      setDeletingFeedName(selectedFeed.name);
      deleteFeedMutation.mutate(undefined);
    }
  };

  return (
    <PageLayout>
      <PageTitle>{t("productFeed.title")}</PageTitle>
      <Typography variant="body" css={cssDescription}>
        {t("productFeed.description")}
      </Typography>
      <Grid
        container
        direction="row"
        justifyContent="end"
        css={cssButtonContainer}
      >
        <Link
          to="/applications/product-feeds/add"
          css={css`
            text-decoration: none;
          `}
        >
          <Button>{t("productFeed.action.addNewFeed")}</Button>
        </Link>
      </Grid>
      <TableContainer
        sx={{
          height: "calc(100vh - 200px)",
          overflowX: "auto",
        }}
      >
        <Table
          aria-label="product feeds table"
          stickyHeader
          sx={{ minWidth: 1000 }}
        >
          <TableHead>
            <TableRow>
              <TableCell width={250}>
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgNote}
                >
                  {t("productFeed.table.column.feedName")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgNote}
                >
                  {t("productFeed.table.column.sourceType")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgNote}
                >
                  {t("productFeed.table.column.status")}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgNote}
                >
                  {t("productFeed.table.column.lastSync")}
                </Typography>
              </TableCell>
              <TableCell align="right" width={120}>
                <Typography
                  variant="body"
                  fontWeight={500}
                  color={theme.colors.staticFgNote}
                >
                  {t("productFeed.table.column.actions")}
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {feedsQuery.isPending ? <TableRowsLoader rowsNum={5} /> : null}
            {!feedsQuery.isPending ? (
              feeds.length === 0 ? (
                <TableRow css={cssEmptyRow}>
                  <TableCell colSpan={5} align="center">
                    <Stack gap="12px">
                      <Empty css={cssEmpty} />
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgNote}
                      >
                        {t("productFeed.table.empty")}
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                feeds.map((feed) => (
                  <TableRow key={feed.id}>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgTitle}
                      >
                        {feed.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgTitle}
                      >
                        <SourceTypeLabel sourceType={feed.sourceType} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={feed.status} />
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body"
                        fontWeight={400}
                        color={theme.colors.staticFgTitle}
                      >
                        {formatDateTime(feed.lastSyncAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing="4px"
                        justifyContent="flex-end"
                      >
                        {feed.status === "partial_success" ||
                        feed.status === "error" ? (
                          <Link
                            to="/applications/product-feeds/$feedId"
                            params={{ feedId: feed.id }}
                            css={css`
                              text-decoration: none;
                            `}
                          >
                            <Button variant="plain" css={cssActionButton}>
                              <Stack
                                direction="row"
                                spacing="4px"
                                sx={{ alignItems: "center" }}
                              >
                                <MotifIcon un-i-motif="warning" />
                                <span>{t("productFeed.action.fix")}</span>
                              </Stack>
                            </Button>
                          </Link>
                        ) : (
                          <Link
                            to="/applications/product-feeds/$feedId"
                            params={{ feedId: feed.id }}
                            css={css`
                              text-decoration: none;
                            `}
                          >
                            <Button variant="plain" css={cssActionButton}>
                              <Stack
                                direction="row"
                                spacing="4px"
                                sx={{ alignItems: "center" }}
                              >
                                <MotifIcon un-i-motif="circle_info" />
                                <span>{t("productFeed.action.view")}</span>
                              </Stack>
                            </Button>
                          </Link>
                        )}
                        <DeleteFeedButton
                          feed={feed}
                          onDeleteClick={handleDeleteClick}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
      {selectedFeed ? (
        <DeleteFeedModal
          open={deleteModalOpen}
          isLoading={deleteFeedMutation.isPending}
          onClose={handleDeleteModalClose}
          onConfirm={handleDeleteModalConfirm}
          feedName={selectedFeed.name}
        />
      ) : null}
    </PageLayout>
  );
};
